'use client';

import { useMemo, useState } from 'react';
import type { RecorridoPunto } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Church, Cross, Route, Ruler, Undo2, Eraser, Info } from 'lucide-react';
import { RouteMapEditor, CARRERA_OFICIAL_COLOR, darkenColor } from '@/components/programa-ss/RouteMapEditor';

export type RecorridoTool = 'iglesia' | 'estacion' | 'ida' | 'vuelta' | null;

/**
 * Alinea el punto nuevo respecto al anterior para que el tramo salga recto:
 * horizontal, vertical o en diagonal exacta de 45°. Se elige el octante más
 * cercano (tan 67,5° ≈ 2.414 es la frontera entre recto y diagonal).
 * Es el mismo criterio que usa el editor de procesiones del CECOP; aquí los
 * puntos van en % del mapa (0–100), no en coordenadas de Minecraft.
 */
export function snapToAxis(prev: RecorridoPunto, p: RecorridoPunto): RecorridoPunto {
  const dx = p.x - prev.x;
  const dy = p.y - prev.y;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ax > ay * 2.414) return { x: p.x, y: prev.y };
  if (ay > ax * 2.414) return { x: prev.x, y: p.y };
  const d = (ax + ay) / 2;
  return { x: prev.x + Math.sign(dx) * d, y: prev.y + Math.sign(dy) * d };
}

export interface RecorridoDraft {
  iglesia: RecorridoPunto | null;
  estacion: RecorridoPunto | null;
  ida: RecorridoPunto[];
  vuelta: RecorridoPunto[];
}

interface Props {
  mapImageUrl: string;
  draft: RecorridoDraft;
  onChange: (next: RecorridoDraft) => void;
  color: string;
  escudoUrl?: string;
  /** Carrera Oficial de referencia. En vísperas no hay: se pasa vacía. */
  carreraOficial?: RecorridoPunto[];
  /**
   * Días de vísperas: no hay Carrera Oficial y en su lugar se marca la iglesia
   * a la que se hace estación de penitencia.
   */
  esVispera: boolean;
  className?: string;
}

const ESTACION_COLOR = '#e11d48';

/**
 * Editor de recorridos de hermandad con las mismas herramientas que el CECOP
 * (iglesia, estación, ida, vuelta, «Recto», deshacer, borrar punto y zoom),
 * pero sobre el mapa de la ciudad y sin la herramienta de recorte.
 */
export function RecorridoEditor({
  mapImageUrl,
  draft,
  onChange,
  color,
  escudoUrl,
  carreraOficial = [],
  esVispera,
  className,
}: Props) {
  const [tool, setTool] = useState<RecorridoTool>(null);
  const [snap, setSnap] = useState(true);

  const hasCarrera = !esVispera && carreraOficial.length >= 2;

  const handleMapClick = (p: RecorridoPunto) => {
    if (!tool) return;
    if (tool === 'iglesia') {
      onChange({ ...draft, iglesia: p });
      setTool(null);
    } else if (tool === 'estacion') {
      onChange({ ...draft, estacion: p });
      setTool(null);
    } else if (tool === 'ida') {
      const prev = draft.ida;
      onChange({ ...draft, ida: [...prev, snap && prev.length ? snapToAxis(prev[prev.length - 1], p) : p] });
    } else if (tool === 'vuelta') {
      const prev = draft.vuelta;
      onChange({ ...draft, vuelta: [...prev, snap && prev.length ? snapToAxis(prev[prev.length - 1], p) : p] });
    }
  };

  // Índices de `segments` que se pintan: 0 = ida, 1 = vuelta.
  const borrarPunto = (segmentIndex: number, pointIndex: number) => {
    if (segmentIndex === 0) onChange({ ...draft, ida: draft.ida.filter((_, i) => i !== pointIndex) });
    else onChange({ ...draft, vuelta: draft.vuelta.filter((_, i) => i !== pointIndex) });
  };

  const markers = useMemo(() => {
    const out = [];
    if (draft.iglesia) out.push({ point: draft.iglesia, color, ring: true, iconUrl: escudoUrl });
    if (draft.estacion) out.push({ point: draft.estacion, color: ESTACION_COLOR, ring: true, cross: true });
    return out;
  }, [draft.iglesia, draft.estacion, color, escudoUrl]);

  const toolBtn = (t: Exclude<RecorridoTool, null>, icon: React.ReactNode, label: string) => (
    <Button
      type="button"
      size="sm"
      variant={tool === t ? 'default' : 'secondary'}
      onClick={() => setTool(tool === t ? null : t)}
      className="gap-1.5"
    >
      {icon}
      {label}
    </Button>
  );

  const ayuda =
    tool === 'ida' || tool === 'vuelta'
      ? 'Clic para ir añadiendo puntos. Con «Recto» activo se alinean en horizontal, vertical o 45°. Clic sobre un punto lo borra.'
      : tool === 'iglesia'
        ? 'Clic sobre el mapa para marcar tu iglesia o sede (de donde sale la cofradía).'
        : tool === 'estacion'
          ? 'Clic sobre el mapa para marcar la iglesia donde hacéis estación de penitencia.'
          : 'Elige una herramienta para empezar a marcar el recorrido.';

  return (
    <div className={className}>
      {/* Barra de herramientas */}
      <div className="flex flex-wrap items-center gap-2">
        {toolBtn('iglesia', <Church className="h-3.5 w-3.5" />, 'Iglesia')}
        {esVispera && toolBtn('estacion', <Cross className="h-3.5 w-3.5" />, 'Estación')}
        {toolBtn('ida', <Route className="h-3.5 w-3.5" />, 'Ida')}
        {toolBtn('vuelta', <Route className="h-3.5 w-3.5" />, 'Vuelta')}

        <span className="mx-1 h-5 w-px bg-white/15" />

        <Button
          type="button"
          size="sm"
          variant={snap ? 'default' : 'outline'}
          onClick={() => setSnap((v) => !v)}
          className="gap-1.5"
          title="Alinea cada punto con el anterior en horizontal, vertical o diagonal de 45°"
        >
          <Ruler className="h-3.5 w-3.5" /> Recto {snap ? 'ON' : 'OFF'}
        </Button>

        {tool === 'ida' && draft.ida.length > 0 && (
          <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={() => onChange({ ...draft, ida: draft.ida.slice(0, -1) })}>
            <Undo2 className="h-3.5 w-3.5" /> Deshacer
          </Button>
        )}
        {tool === 'vuelta' && draft.vuelta.length > 0 && (
          <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={() => onChange({ ...draft, vuelta: draft.vuelta.slice(0, -1) })}>
            <Undo2 className="h-3.5 w-3.5" /> Deshacer
          </Button>
        )}

        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto gap-1.5 text-destructive"
          onClick={() => onChange({ iglesia: null, estacion: null, ida: [], vuelta: [] })}
        >
          <Eraser className="h-3.5 w-3.5" /> Empezar de nuevo
        </Button>
      </div>

      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
        {ayuda}
      </p>

      <RouteMapEditor
        mapImageUrl={mapImageUrl}
        onMapClick={tool ? handleMapClick : undefined}
        reference={hasCarrera ? { puntos: carreraOficial, color: CARRERA_OFICIAL_COLOR } : null}
        referenceEndpointMarkers={hasCarrera}
        segments={[
          { puntos: draft.ida, color },
          { puntos: draft.vuelta, color: darkenColor(color), dashed: true },
        ]}
        pointHandles={!!tool}
        onSegmentPointClick={borrarPunto}
        markers={markers}
        className="mt-3 h-[52vh]"
      />

      {/* Leyenda */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.7rem] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <svg width="22" height="6">
            <line x1="1" y1="3" x2="21" y2="3" stroke={color} strokeWidth={3} strokeLinecap="round" />
          </svg>
          Ida
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="22" height="6">
            <line x1="1" y1="3" x2="21" y2="3" stroke={darkenColor(color)} strokeWidth={3} strokeDasharray="5 3" strokeLinecap="round" />
          </svg>
          Vuelta
        </span>
        <span className="flex items-center gap-1.5">
          <Church className="h-3 w-3" style={{ color }} /> Tu iglesia
        </span>
        {esVispera ? (
          <span className="flex items-center gap-1.5">
            <Cross className="h-3 w-3" style={{ color: ESTACION_COLOR }} /> Estación de penitencia
          </span>
        ) : (
          hasCarrera && (
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-5 rounded-full" style={{ background: CARRERA_OFICIAL_COLOR }} /> Carrera Oficial
            </span>
          )
        )}
      </div>
    </div>
  );
}
